import { useState, useEffect } from "react";
import { ArrowRight, Info, Wifi, Coffee, Droplets, Toilet, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SkeletonHero } from "@/components/ui/skeleton-card";
import { siteConfig } from "@/config/siteConfig";
import { useNavigate } from "react-router-dom";
import Booking from "@/components/Booking";
import heroBus from "@/assets/hero-bus.jpg";

export function HeroSection() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    // Simulate content loading
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  if (isLoading) {
    return (
      <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
        <SkeletonHero />
      </section>
    );
  }

  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src={heroBus}
          alt="Luxury coach bus on highway"
          className="w-full h-full object-cover transition-opacity duration-500"
          onLoad={handleImageLoad}
          style={{ opacity: imageLoaded ? 1 : 0 }}
        />
        {/* Gradient overlays */}
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/90 to-background/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
      </div>

      {/* Floating particles / decorative elements */}
      <div className="absolute inset-0 z-10 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-primary/10 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl animate-float animation-delay-200" />
      </div>

      {/* Content */}
      <div className="relative z-20 w-full">
        <div className="container mx-auto px-4 pt-32 pb-24">
          <div className="max-w-3xl mb-16">
            {/* Badge */}
            <div 
              onClick={() => navigate("/routes")}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass neon-border mb-8 animate-fade-in-up cursor-pointer hover:border-primary/70 transition-colors"
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
              </span>
              <span className="text-sm font-medium text-foreground">View all routes</span>
            </div>

            {/* Heading */}
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-6 animate-fade-in-up animation-delay-100">
              Experience <span className="text-gradient">{siteConfig.companyName}</span>
            </h1>

            {/* Subheading */}
            <p className="text-lg md:text-xl text-muted-foreground max-w-xl mb-10 animate-fade-in-up animation-delay-200">
              Experience hassle-free border crossing with spacious seating, climate control, and dedicated service spanning two major cities and inland destinations.
            </p>

            {/* Amenities Icons */}
            <div className="flex flex-wrap gap-8 mb-10 animate-fade-in-up animation-delay-250">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                  <Wifi className="w-5 h-5 text-primary" />
                </div>
                <span className="text-sm font-medium text-foreground">Free WiFi Onboard</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-secondary/20 flex items-center justify-center">
                  <Coffee className="w-5 h-5 text-secondary" />
                </div>
                <span className="text-sm font-medium text-foreground">Complimentary Snacks</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                  <Toilet className="w-5 h-5 text-primary" />
                </div>
                <span className="text-sm font-medium text-foreground">Restrooms Onboard</span>
              </div>
            </div>
          </div>

          {/* Booking Component - Full container width, no max-width constraint */}
          {siteConfig.onSiteBookingEnabled && (
            <div className="w-full mb-10 animate-fade-in-up animation-delay-300">
              <Booking />
            </div>
          )}

          {/* Book Now Button - External Booking Link */}
          {!siteConfig.onSiteBookingEnabled && siteConfig.ecomBookingEnabled && (
            <div className="mb-10 animate-fade-in-up animation-delay-300">
              <a 
                href={siteConfig.ecomBookingUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-10 py-4 text-lg font-bold text-white rounded-xl transition-all duration-300 hover:scale-105 active:scale-95 shadow-xl hover:shadow-2xl group relative overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, hsl(347 76% 39%) 0%, hsl(31 88% 58%) 100%)',
                  boxShadow: '0 0 30px -8px hsl(347 76% 39% / 0.5)'
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="relative flex items-center gap-3">
                  <ArrowRight className="w-6 h-6 transition-transform duration-300 group-hover:translate-x-1" />
                  <span>Book Now</span>
                </div>
                <div className="absolute inset-0 border-2 border-white/10 rounded-xl" />
              </a>
            </div>
          )}

          {/* Learn More CTA - Only show if either booking method is enabled */}
          {(siteConfig.onSiteBookingEnabled || siteConfig.ecomBookingEnabled) && (
            <div className="animate-fade-in-up animation-delay-300">
              <Button
                asChild
                variant="outline"
                size="lg"
                className="border-border/50 hover:border-primary/50 hover:bg-primary/10 font-semibold px-8 h-14 text-base glass"
              >
                <a href="#about">
                  <Info className="mr-2 h-5 w-5" />
                  Learn More About Us
                </a>
              </Button>
            </div>
          )}

          {/* Stats */}
          <div className="grid grid-cols-3 gap-8 mt-16 pt-8 border-t border-border/30 animate-fade-in-up animation-delay-400">
            {siteConfig.stats.map((stat) => (
              <div key={stat.label}>
                <div className="flex items-center gap-2">
                  <div className="text-2xl md:text-3xl font-display font-bold text-gradient">{stat.value}</div>
                  {stat.label === "On-Time Record" && <CheckCircle className="w-5 h-5 text-green-500" />}
                </div>
                <div className="text-sm text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll indicator
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 animate-fade-in animation-delay-500">
        <div className="flex flex-col items-center gap-2 text-muted-foreground">
          <span className="text-xs uppercase tracking-widest">Scroll</span>
          <div className="w-6 h-10 rounded-full border-2 border-muted-foreground/30 flex justify-center pt-2">
            <div className="w-1.5 h-1.5 rounded-full bg-primary animate-bounce" />
          </div>
        </div>
      </div> */}
    </section>
  );
}
